import { useState, useEffect } from 'react';
import { supabase } from '@/integrations/supabase/client';
import type { CartItem } from './useCartState';

type DatabaseCartItem = {
  id: string;
  user_id: string;
  product_id: string;
  quantity: number;
  created_at: string;
  updated_at: string;
};

export const useCartDatabase = () => {
  const [isLoading, setIsLoading] = useState(false);

  // Sync cart items to database
  const syncCartToDatabase = async (cartItems: CartItem[]) => {
    try {
      const { data: { session } } = await supabase.auth.getSession();
      if (!session?.user) return;

      setIsLoading(true);

      // Get current database cart items
      const { data: currentItems } = await supabase
        .from('cart_items')
        .select('*')
        .eq('user_id', session.user.id);

      // Items to upsert (add or update)
      const itemsToUpsert = cartItems.map(item => ({
        user_id: session.user.id,
        product_id: item.product.id,
        quantity: item.quantity
      }));

      // Items to delete (not in current cart but in database)
      const currentProductIds = cartItems.map(item => item.product.id);
      const itemsToDelete = currentItems?.filter(
        item => !currentProductIds.includes(item.product_id)
      ) || [];

      // Perform upserts
      if (itemsToUpsert.length > 0) {
        await supabase
          .from('cart_items')
          .upsert(itemsToUpsert, {
            onConflict: 'user_id,product_id'
          });
      }

      // Perform deletes
      if (itemsToDelete.length > 0) {
        await supabase
          .from('cart_items')
          .delete()
          .in('id', itemsToDelete.map(item => item.id));
      }

      console.log('✅ Cart synced to database');
    } catch (error) {
      console.error('❌ Failed to sync cart to database:', error);
    } finally {
      setIsLoading(false);
    }
  };

  // Load cart items from database
  const loadCartFromDatabase = async (products: any[]): Promise<CartItem[]> => {
    try {
      const { data: { session } } = await supabase.auth.getSession();
      if (!session?.user) return [];

      setIsLoading(true);

      const { data: dbCartItems, error } = await supabase
        .from('cart_items')
        .select('*')
        .eq('user_id', session.user.id)
        .order('created_at', { ascending: true });

      if (error) throw error;

      // Convert database items to CartItem format
      const cartItems: CartItem[] = [];
      for (const dbItem of dbCartItems || []) {
        const product = products.find(p => p.id === dbItem.product_id);
        if (product) {
          cartItems.push({
            id: dbItem.product_id,
            quantity: dbItem.quantity,
            product
          });
        }
      }

      console.log('📦 Loaded cart from database:', cartItems.length, 'items');
      return cartItems;
    } catch (error) {
      console.error('❌ Failed to load cart from database:', error);
      return [];
    } finally {
      setIsLoading(false);
    }
  };

  // Clear cart from database
  const clearCartFromDatabase = async () => {
    try {
      const { data: { session } } = await supabase.auth.getSession();
      if (!session?.user) return;

      await supabase
        .from('cart_items')
        .delete()
        .eq('user_id', session.user.id);

      console.log('🧹 Cart cleared from database');
    } catch (error) {
      console.error('❌ Failed to clear cart from database:', error);
    }
  };

  // Clean up old cart items (30+ days)
  const cleanupOldCartItems = async () => {
    try {
      const thirtyDaysAgo = new Date();
      thirtyDaysAgo.setDate(thirtyDaysAgo.getDate() - 30);

      await supabase
        .from('cart_items')
        .delete()
        .lt('created_at', thirtyDaysAgo.toISOString());

      console.log('🧹 Old cart items cleaned up');
    } catch (error) {
      console.error('❌ Failed to cleanup old cart items:', error);
    }
  };

  return {
    syncCartToDatabase,
    loadCartFromDatabase,
    clearCartFromDatabase,
    cleanupOldCartItems,
    isLoading
  };
};